import {
  dimensionById,
  enumerateValues,
  missingScoreLabel,
  type DimensionId,
} from './dimensions';
import { EPSILON, MISSING_SCORE_PREFIX } from './filters';
import type { FilterChip, RangeChip } from './filters';
import { DOMAIN_KEYS, DOMAIN_LABELS, type RosterDomainKey } from './rosterRows';

function isDomainKey(value: string): value is RosterDomainKey {
  return DOMAIN_KEYS.includes(value as RosterDomainKey);
}

function valueLabel(id: DimensionId, value: string): string {
  if (id === 'dataQuality') {
    const domain = value.slice(MISSING_SCORE_PREFIX.length);
    return isDomainKey(domain) ? missingScoreLabel(domain) : value;
  }
  return enumerateValues(id, []).find((option) => option.value === value)?.label ?? value;
}

function formatBound(value: number, step: number): string {
  if (step >= 1) return String(value);
  return value.toFixed(1);
}

function rangeName(chip: RangeChip, fallback: string): string {
  if (chip.dimension === 'domainPL') return `${DOMAIN_LABELS[chip.domain]} PL`;
  return fallback;
}

export function filterChipLabel(chip: FilterChip): string {
  const dimension = dimensionById(chip.dimension);
  const label = dimension?.label ?? chip.dimension;

  if ('values' in chip) {
    return `${label}: ${chip.values.map((value) => valueLabel(chip.dimension, value)).join(', ')}`;
  }

  const name = rangeName(chip, label);
  const step = dimension?.kind === 'range' ? dimension.scale.step : 1;
  const max = dimension?.kind === 'range' ? dimension.scale.max : Infinity;

  const { from, to } = chip;
  if (from == null) {
    return to == null ? name : `${name}: ≤ ${formatBound(to, step)}`;
  }
  if (to == null || to >= max - EPSILON) return `${name}: ≥ ${formatBound(from, step)}`;
  return `${name}: ${formatBound(from, step)}–${formatBound(to, step)}`;
}
